import { CircularProgress } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface Props {
  data: Array<{ x: string; yleft: number; yright?: number }>;
  width?: number;
  height?: number;
  yLabelLeft?: string;
  yLabelRight?: string;
  yTicksLeftFormatter?: (tick: number) => string;
  yTicksRightFormatter?: (tick: number) => string;
  colorYLeft?: string;
  colorYRight?: string;
  xTickLabelHeight?: number;
  xTickLabelMaxLength?: number;
  tooltipYValueFormatter?: (value: number, name: "yleft" | "yright") => string;
  barOpacity: number;
}

const defaultXTickLabelHeight = 150;

const BarChartRecharts: React.FC<Props> = ({
  data,
  width,
  height,
  yLabelLeft,
  yLabelRight,
  yTicksLeftFormatter,
  yTicksRightFormatter,
  colorYLeft,
  colorYRight,
  xTickLabelHeight,
  xTickLabelMaxLength,
  tooltipYValueFormatter,
  barOpacity,
}: Props) => {
  const leftAxisColors = ["#666666", "#3c78aa"];
  const rightAxisColor = "#a484ad";

  const xAxisColor = "#666666";

  const [processedData, setProcessedData] = useState<
    Array<{ x: string; yleft: number; xlabel: string; yright?: number }>
  >([]);

  const [hasRightAxis, setHasRightAxis] = useState(false);

  useEffect(() => {
    if (data.length && data[0].yright !== undefined) {
      setHasRightAxis(true);
    }
    const shortenXLabel = (xlabel: string) =>
      xlabel.length > (xTickLabelMaxLength ?? 20)
        ? xlabel.slice(0, xTickLabelMaxLength ?? 20) + "..."
        : xlabel;
    setProcessedData(data.map((d) => ({ ...d, xlabel: shortenXLabel(d.x) })));
  }, [data, xTickLabelMaxLength]);

  const getCurveName = (dataKey: "yleft" | "yright") => {
    if (dataKey === "yleft") {
      return yLabelLeft ?? "y (left)";
    } else {
      return yLabelRight ?? "y (right)";
    }
  };

  const getHexBarOpacity = () => {
    const opacity = barOpacity
      ? Math.round(barOpacity * 255).toString(16)
      : "AA";
    return opacity.length === 1 ? `0${opacity}` : opacity;
  };

  const opacity = getHexBarOpacity();

  const setOpacity = (color: string) => `${color}${opacity}`;

  return (
    <ResponsiveContainer width={width ?? "100%"} height={height ?? 400}>
      {processedData.length ? (
        <BarChart
          data={processedData}
          margin={{ left: 50, top: 50, right: 50 }}
          style={{ fontSize: "1rem" }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            type="category"
            angle={-45}
            textAnchor="end"
            interval={0}
            height={xTickLabelHeight ?? defaultXTickLabelHeight}
            tickFormatter={(tick, index) => processedData[index].xlabel}
            fill={xAxisColor}
          />
          <YAxis
            yAxisId="left"
            fill={
              colorYLeft ??
              (hasRightAxis ? leftAxisColors[1] : leftAxisColors[0])
            }
            tick={{
              fill:
                colorYLeft ??
                (hasRightAxis ? leftAxisColors[1] : leftAxisColors[0]),
            }}
            tickFormatter={yTicksLeftFormatter}
          >
            {yLabelLeft && (
              <Label
                value={yLabelLeft}
                offset={5}
                position="left"
                angle={-90}
                style={{ textAnchor: "middle" }}
                fill={
                  colorYLeft ??
                  (hasRightAxis ? leftAxisColors[1] : leftAxisColors[0])
                }
              />
            )}
          </YAxis>
          {hasRightAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              fill={colorYRight ?? rightAxisColor}
              tick={{
                fill: colorYRight ?? rightAxisColor,
              }}
              tickFormatter={yTicksRightFormatter}
            >
              {yLabelRight && (
                <Label
                  value={yLabelRight}
                  offset={5}
                  position="right"
                  angle={-90}
                  style={{ textAnchor: "middle" }}
                  fill={colorYRight ?? rightAxisColor}
                />
              )}
            </YAxis>
          )}
          <Tooltip
            formatter={(value: number, name: "yleft" | "yright") =>
              tooltipYValueFormatter
                ? [tooltipYValueFormatter(value, name), getCurveName(name)]
                : [value, getCurveName(name)]
            }
          />
          <Bar
            yAxisId="left"
            dataKey="yleft"
            fill={
              colorYLeft
                ? setOpacity(colorYLeft)
                : setOpacity(leftAxisColors[1])
            }
          />
          {hasRightAxis && (
            <Bar
              yAxisId="right"
              dataKey="yright"
              fill={
                colorYRight
                  ? setOpacity(colorYRight)
                  : setOpacity(rightAxisColor)
              }
            />
          )}
        </BarChart>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </div>
      )}
    </ResponsiveContainer>
  );
};

export default BarChartRecharts;



// WEBPACK FOOTER //
// ./src/components/generic/BarChart.tsx